import { useCallback, useEffect, useRef, useState } from 'react';

interface ScrollOverflowState {
  hasOverflow: boolean;
  isScrolledLeft: boolean;
  isScrolledRight: boolean;
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
}

/**
 * Custom hook to detect horizontal scroll overflow and track scroll position
 * @param ref - React ref to the scrollable container element
 * @param scrollThreshold - Minimum scroll amount to consider as "scrolled" (default: 5px)
 * @returns Object containing overflow and scroll state information
 */
export const useScrollOverflow = (ref: React.RefObject<HTMLElement>, scrollThreshold = 5) => {
  const [state, setState] = useState<ScrollOverflowState>({
    hasOverflow: false,
    isScrolledLeft: false,
    isScrolledRight: false,
    scrollLeft: 0,
    scrollWidth: 0,
    clientWidth: 0,
  });

  // Use ref to store timeout for debouncing
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();

  const updateScrollState = useCallback(() => {
    if (!ref.current) return;

    const element = ref.current;
    const scrollLeft = element.scrollLeft;
    const scrollWidth = element.scrollWidth;
    const clientWidth = element.clientWidth;

    // Check if there's overflow
    const hasOverflow = scrollWidth > clientWidth;

    // Check if scrolled from left (with threshold)
    const isScrolledLeft = scrollLeft > scrollThreshold;

    // Check if scrolled to right (with threshold)
    const maxScroll = scrollWidth - clientWidth;
    const isScrolledRight = scrollLeft >= maxScroll - scrollThreshold;

    setState({
      hasOverflow,
      isScrolledLeft,
      isScrolledRight,
      scrollLeft,
      scrollWidth,
      clientWidth,
    });
  }, [ref, scrollThreshold]);

  // Handle scroll events with debouncing
  const handleScroll = useCallback(() => {
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Set new timeout for debounced update
    scrollTimeoutRef.current = setTimeout(() => {
      updateScrollState();
    }, 10); // 10ms debounce
  }, [updateScrollState]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Initial state update
    updateScrollState();

    // Set up ResizeObserver to detect size changes
    const resizeObserver = new ResizeObserver(() => {
      updateScrollState();
    });

    // Observe the element
    resizeObserver.observe(element);

    // Add scroll event listener
    element.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup
    return () => {
      resizeObserver.disconnect();
      element.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [ref, handleScroll, updateScrollState]);

  // Also update on window resize
  useEffect(() => {
    const handleResize = () => {
      updateScrollState();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateScrollState]);

  return state;
};
